import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, Globe } from "lucide-react";
import type { Question } from "../../admin/hooks/useQuestionsManagement";
import { useLanguage } from "../../../context/LanguageContext";

interface QuestionsSectionProps {
  questions: Question[];
  questionAnswers: Record<number, boolean>;
  isLoading: boolean;
  onAnswer: (id: number, value: boolean) => void;
}

export default function QuestionsSection({
  questions,
  questionAnswers,
  isLoading,
  onAnswer,
}: QuestionsSectionProps) {
  const { t } = useLanguage();
  const allAnswered =
    questions.length === 0 ||
    questions.every((q) => q.id in questionAnswers);

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center py-8 gap-2 text-gray-500"
      >
        <Loader2 size={20} className="animate-spin" />
        <span className="text-sm">{t("auth.questionsSection.loading")}</span>
      </motion.div>
    );
  }

  if (questions.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="space-y-4"
    >
      <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2">
        <Globe size={18} className="text-[#b3d130]" />
        {t("auth.questionsSection.title")}
        <span className="text-xs font-normal text-gray-500 ml-1">
          {t("auth.questionsSection.allRequired")}
        </span>
      </h4>

      {questions.map((q, i) => (
        <motion.div
          key={q.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 + i * 0.05 }}
          className="p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-gray-200 hover:border-[#b3d130]/30 transition-colors"
        >
          <p className="text-sm font-semibold text-gray-800 mb-4">
            <span className="text-[#b3d130] mr-2 font-bold">{i + 1}.</span>
            {q.question}
          </p>

          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={() => onAnswer(q.id, true)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm border-2 transition-all ${questionAnswers[q.id] === true
                ? "border-green-500 bg-green-50 text-green-700 shadow-md shadow-green-200"
                : "border-gray-200 bg-white text-gray-600 hover:border-green-300 hover:bg-green-50/30"
                }`}
            >
              <CheckCircle size={16} /> {t("common.yes")}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={() => onAnswer(q.id, false)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm border-2 transition-all ${questionAnswers[q.id] === false
                ? "border-red-400 bg-red-50 text-red-600 shadow-md shadow-red-200"
                : "border-gray-200 bg-white text-gray-600 hover:border-red-300 hover:bg-red-50/30"
                }`}
            >
              <XCircle size={16} /> {t("common.no")}
            </motion.button>
          </div>
        </motion.div>
      ))}

      {!allAnswered && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2"
        >
          <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
          <p className="text-sm font-medium text-amber-700">
            {t("auth.questionsSection.answerAllToContinue")}
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}
